import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, LayoutDashboard, UserRound } from 'lucide-react';
import { SETTINGS_SCOPES } from '@/constant/settings-menu';
import { useCurrentOrganizationName } from '@/hooks/use-organizations';
import { SIDEBAR_NAV_ICON_SIZE } from '@/components/core';
import { SearchableSelect } from '@/components/core/searchable-select/searchable-select';
import { APP_PATHS } from '@/constant/app-paths';
import { usePermittedSidebar } from '@/hooks/use-permitted-sidebar';
import { cn } from '@/lib/utils';
import { matchesSidebarPath } from '@/lib/utils/sidebar-utils';
import { SettingsSection } from '@/types/sidebar.type';
import { SoonBadge } from '../soon-badge/soon-badge';

/** The settings group holding `pathname`, by the longest matching page path. */
const groupOf = (sections: SettingsSection[], pathname: string) =>
  sections
    .flatMap((section) => section.items.map((item) => ({ section, item })))
    .filter(({ item }) => matchesSidebarPath(pathname, item.path))
    .sort((a, b) => b.item.path.length - a.item.path.length)[0];

/**
 * The Settings area: a pane of the user's settings groups beside the open page (`<Outlet />`). A group
 * opens and closes on its own; the one holding the open page starts open. Below 1024px the pane gives
 * way to a page picker above the page.
 */
export const SettingsLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { settingsSections } = usePermittedSidebar();
  const current = groupOf(settingsSections, pathname);
  const isOverview = pathname === APP_PATHS.SETTINGS;
  const { data: organizationName } = useCurrentOrganizationName();
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());

  // Opening a page opens its group (others keep the state the user gave them).
  useEffect(() => {
    if (!current) return;
    setOpenGroups((open) =>
      open.has(current.section.title) ? open : new Set(open).add(current.section.title)
    );
  }, [current]);

  const toggle = (title: string) =>
    setOpenGroups((open) => {
      const next = new Set(open);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });

  // Below 1024px: one searchable picker of every settings page (each with its group, which the search
  // also matches), so the overview needs no search box of its own there. It sits above the page.
  const pagePicker = (
    <div className="lg:hidden">
      <SearchableSelect
        aria-label={t('SETTINGS_PAGE')}
        className="w-full sm:w-80"
        value={current?.item.path ?? APP_PATHS.SETTINGS}
        onChange={(path) => path && navigate(path)}
        placeholder={t('OVERVIEW')}
        searchPlaceholder={t('SEARCH_SETTINGS')}
        emptyText={t('NO_SETTINGS_MATCH')}
        options={[
          { value: APP_PATHS.SETTINGS, label: t('OVERVIEW') },
          ...settingsSections.flatMap((section) =>
            section.items.map((item) => ({
              value: item.path,
              label: item.soon ? `${t(item.name)} · ${t('SOON')}` : t(item.name),
              description: t(section.title),
            }))
          ),
        ]}
      />
    </div>
  );

  return (
    // The outer div is the page root the main layout styles as its scroll container (a flex column);
    // the inner one holds the pane beside the page. Without it the layout's `display:flex` won over the
    // grid and squeezed the pane to a sliver.
    // From 1024px the page root stops scrolling as a whole: the pane stays where it is and only the page
    // column beside it scrolls (the main layout's padding below moves into that column).
    // Also from 1024px: the settings pane starts 24px from the sidebar instead of the layout centring
    // the content (which left a wide gap on big screens); the content still stops at 1360px.
    <div className="lg:!overflow-hidden lg:!pb-0 lg:!pl-6 lg:!pr-[max(1.5rem,calc(100%-1360px-1.5rem))]">
      <div className="flex w-full shrink-0 flex-col gap-4 lg:grid lg:h-full lg:min-h-0 lg:grid-cols-[240px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] lg:gap-8">
        <nav
          aria-label={t('SETTINGS')}
          className="hidden max-h-full content-start gap-0.5 self-start overflow-y-auto rounded-lg border border-border bg-card p-2 lg:grid"
        >
          {/* Prototype `settingsPane`: whose settings these are, then Overview first. */}
          <div className="mb-1 flex items-center gap-2.5 border-b px-2 pb-3 pt-1">
            <span
              aria-hidden
              className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-sm font-semibold text-primary-foreground"
            >
              {(organizationName ?? '?').charAt(0).toUpperCase()}
            </span>
            <div className="grid min-w-0">
              <b className="truncate text-sm font-semibold">{organizationName ?? '…'}</b>
              <span className="truncate text-xs text-muted-foreground">
                {t('ORGANIZATION_SETTINGS')}
              </span>
            </div>
          </div>
          <Link
            to={APP_PATHS.SETTINGS}
            aria-current={isOverview ? 'page' : undefined}
            className={cn(
              'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
              isOverview && 'bg-accent text-primary hover:bg-accent'
            )}
          >
            <LayoutDashboard size={SIDEBAR_NAV_ICON_SIZE} className="shrink-0" />
            {t('OVERVIEW')}
          </Link>
          {SETTINGS_SCOPES.map((scope) => (
            <Fragment key={scope}>
              <span className="px-2.5 pb-1 pt-3.5 text-xs font-medium text-muted-foreground">
                {t(scope)}
              </span>
              {settingsSections
                .filter((section) => section.scope === scope)
                .map((section) => {
                  const isOpen = openGroups.has(section.title);
                  const isCurrent = current?.section.title === section.title;
                  const listId = `settings-group-${section.title}`;
                  return (
                    <div key={section.title}>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={listId}
                        onClick={() => toggle(section.title)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted',
                          isCurrent && 'text-primary'
                        )}
                      >
                        <span className="min-w-0 flex-1 truncate">{t(section.title)}</span>
                        <ChevronRight
                          className={cn(
                            'size-4 shrink-0 text-muted-foreground transition-transform',
                            isOpen && 'rotate-90'
                          )}
                        />
                      </button>
                      {isOpen && (
                        <ul id={listId} className="grid gap-0.5 py-0.5 pl-7">
                          {section.items.map((item) => {
                            const isActive = current?.item.id === item.id;
                            return (
                              <li key={item.id}>
                                <Link
                                  to={item.path}
                                  aria-current={isActive ? 'page' : undefined}
                                  className={cn(
                                    'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-foreground transition-colors hover:bg-muted',
                                    isActive && 'bg-accent font-medium text-primary hover:bg-accent'
                                  )}
                                >
                                  <span className="min-w-0 flex-1 truncate">{t(item.name)}</span>
                                  {item.soon && <SoonBadge />}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}
            </Fragment>
          ))}
          <span className="px-2.5 pb-1 pt-3.5 text-xs font-medium text-muted-foreground">
            {t('PERSONAL')}
          </span>
          <Link
            to={APP_PATHS.MY_PROFILE}
            className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            <UserRound size={SIDEBAR_NAV_ICON_SIZE} className="shrink-0" />
            <span className="min-w-0 flex-1 truncate">{t('MY_PROFILE')}</span>
            <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
          </Link>
        </nav>

        {pagePicker}

        <div className="min-w-0 lg:-mr-2 lg:h-full lg:overflow-y-auto lg:overscroll-contain lg:pb-16 lg:pr-2 lg:hrm-scrollbar">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
